// src/screens/PassDetailsPage.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Center, Container, Loader, Text, Flex, Box, Image } from "@mantine/core";
import Lottie from "lottie-react";

import wclogoAnimation from "../assets/lottie/wclogo.json";

import { getSitePasses } from "../atoms/passNetwork";

import PassWelcomeStepView from "./pass/PassWelcomeStepView";
import PassEnterOTPView from "./pass/PassEnterOTPView";
import PassExtraDetailsView from "./pass/PassExtraDetailsView";
import PassPurchaseStepView from "./pass/PassPurchaseStepView";
import PassSuccessView from "./pass/PassSuccessView";

const Flow = {
    LOADING: "LOADING",
    ERROR: "ERROR",
    WELCOME: "WELCOME",
    OTP: "OTP",
    EXTRA: "EXTRA",
    PURCHASE: "PURCHASE",
    SUCCESS: "SUCCESS",
};

const S = {
    fullHeightContainer: { minHeight: "100vh" },
    logoCircleWrapper: (size = 175) => ({
        position: "relative",
        width: size,
        height: size,
        marginTop: 110,
        zIndex: 1,
    }),
    logoCircleBg: (size = 175) => ({
        position: "absolute",
        top: 0,
        left: 0,
        width: size,
        height: size,
        borderRadius: "50%",
        backgroundColor: "white",
        zIndex: 0,
    }),
    bgImage: { position: "absolute", top: 0, left: 0, zIndex: 0 },
};

const LogoWithCircle = React.memo(function LogoWithCircle({ showAnimatedLogo }) {
    const size = 175;
    return (
        <Flex justify="center" align="center" style={S.logoCircleWrapper(size)}>
            <Box style={S.logoCircleBg(size)} />
            <Lottie
                animationData={wclogoAnimation}
                loop={false}
                autoplay
                style={{
                    position: "relative",
                    width: size - 16,
                    height: size - 16,
                    zIndex: 2,
                    paddingRight: 5,
                }}
            />
        </Flex>
    );
});

// A small wrapper you can reuse in each Pass step
function PassHeader({ backgroundUrl }) {
    const imageHeight = "220px";
    return (
        <Flex justify="center" style={{ position: "relative", overflow: "hidden" }}>
            {backgroundUrl ? (
                <Image src={backgroundUrl} width="100%" height={imageHeight} style={S.bgImage} />
            ) : null}
            <LogoWithCircle showAnimatedLogo />
        </Flex>
    );
}

export default function PassDetailsPage() {
    // IMPORTANT: your route is /passes/:siteID (capital D)
    const { siteID } = useParams();
    const siteId = siteID;

    const [flow, setFlow] = useState(Flow.LOADING);

    // Site info
    const [siteTitle, setSiteTitle] = useState("loading site...");
    const [siteTimezone, setSiteTimezone] = useState("Australia/Hobart");
    const [siteImageUrl, setSiteImageUrl] = useState("");
    const [maxDuration, setMaxDuration] = useState(1);
    const [passes, setPasses] = useState([]);

    // Collected data
    const [name, setName] = useState("");
    const [phoneE164, setPhoneE164] = useState("");
    const [registrationNumber, setRegistrationNumber] = useState("");
    const [address, setAddress] = useState("");

    // Purchase result
    const [paidNights, setPaidNights] = useState(0);
    const [paidTotal, setPaidTotal] = useState(0);
    const [arrivalDate, setArrivalDate] = useState(new Date());
    const [passCode, setPassCode] = useState("");
    const [purchasedPassId, setPurchasedPassId] = useState(0);

    // Extend flow
    const [extendPassId, setExtendPassId] = useState(0);

    // Errors
    const [siteLoadError, setSiteLoadError] = useState("");

    const fallbackImageUrl = "https://placehold.co/1280x720?text=West+Coast+Camping";
    const imageUrl = useMemo(() => (siteImageUrl ? siteImageUrl : fallbackImageUrl), [siteImageUrl]);

    const setRegistrationNumberUpper = (v) => {
        setRegistrationNumber(String(v || "").toUpperCase().replace(/\s+/g, ""));
    };

    useEffect(() => {
        let cancelled = false;

        async function load() {
            try {
                setFlow(Flow.LOADING);
                setSiteLoadError("");

                if (!siteId) {
                    throw new Error("Missing siteID in URL.");
                }

                const res = await getSitePasses(siteId);

                const statusOk = Number(res?.status) === 1;
                const data = res?.data || {};
                const site = data?.site || {};
                const passesRaw = Array.isArray(data?.passes) ? data.passes : [];

                if (!statusOk) {
                    throw new Error(data?.message || "Couldn’t load site right now.");
                }

                if (cancelled) return;

                setSiteTitle(site?.title || "Camping");
                setSiteTimezone(site?.timezone || "Australia/Hobart");
                setMaxDuration(Number(site?.max_pass_duration || 1));
                setSiteImageUrl(site?.image_remote_url || "");

                const mapped = passesRaw
                    .map((p) => ({
                        id: Number(p?.id || 0),
                        title: String(p?.title || ""),
                        description: String(p?.description || ""),
                        duration: Number(p?.duration || 0),
                        cost: Number(p?.cost || 0),
                    }))
                    .filter((p) => p.id > 0 && p.duration > 0);

                setPasses(mapped);
                setFlow(Flow.WELCOME);
            } catch (e) {
                if (cancelled) return;
                setSiteLoadError(e?.message || "Couldn’t load site right now.");
                setFlow(Flow.ERROR);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [siteId]);

    function resetAll() {
        setName("");
        setPhoneE164("");
        setRegistrationNumber("");
        setAddress("");

        setPaidNights(0);
        setPaidTotal(0);
        setArrivalDate(new Date());

        setPassCode("");
        setPurchasedPassId(0);
        setExtendPassId(0);

        setFlow(Flow.WELCOME);
    }

    function handlePurchaseSuccess(pass) {
        const id = Number(pass?.id || 0);
        setPurchasedPassId(id);

        setPaidNights(Number(pass?.duration || 0));

        const cost =
            typeof pass?.cost === "number"
                ? pass.cost
                : typeof pass?.cost === "string"
                    ? Number(pass.cost)
                    : typeof pass?.cost_cents === "number"
                        ? pass.cost_cents / 100
                        : 0;

        setPaidTotal(Number.isFinite(cost) ? cost : 0);

        if (pass?.car_registration) setRegistrationNumber(String(pass.car_registration));
        if (pass?.car_address) setAddress(String(pass.car_address));
        if (pass?.code) setPassCode(String(pass.code));

        setArrivalDate(new Date());
        setFlow(Flow.SUCCESS);
    }

    // ─────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────
    if (flow === Flow.LOADING) {
        return (
            <Container size={700} px="md" style={{ height: "100vh" }}>
                <Center style={{ height: "100%" }}>
                    <div style={{ textAlign: "center" }}>
                        <Loader />
                    </div>
                </Center>
            </Container>
        );
    }

    if (flow === Flow.ERROR) {
        return (
            <Container size={700} px="md" style={{ height: "100vh" }}>
                <Center style={{ height: "100%" }}>
                    <div style={{ textAlign: "center" }}>
                        <Text c="red" fw={700}>
                            {siteLoadError || "Couldn’t load site."}
                        </Text>
                        <Text c="dimmed" mt="sm">
                            Please check the link and try again.
                        </Text>
                    </div>
                </Center>
            </Container>
        );
    }

    const HeaderLogo = <PassHeader backgroundUrl={siteImageUrl || imageUrl} />;

    if (flow === Flow.WELCOME) {
        return (
            <PassWelcomeStepView
                HeaderLogo={HeaderLogo}
                siteId={siteId}
                imageUrl={imageUrl}
                siteTitle={siteTitle}
                itemTitle={passes?.[0]?.title || "Camping Pass"}
                onContinue={({ name: n, phoneE164: p }) => {
                    setName(n);
                    setPhoneE164(p);
                    setFlow(Flow.EXTRA);
                }}
                onRequireOTP={({ name: n, phoneE164: p }) => {
                    setName(n);
                    setPhoneE164(p);
                    setFlow(Flow.OTP);
                }}
            />
        );
    }

    if (flow === Flow.OTP) {
        return (
            <PassEnterOTPView
                HeaderLogo={HeaderLogo}
                siteId={siteId}
                name={name}
                phoneE164={phoneE164}
                onBack={() => setFlow(Flow.WELCOME)}
                onVerified={() => setFlow(Flow.EXTRA)}
            />
        );
    }

    if (flow === Flow.EXTRA) {
        return (
            <PassExtraDetailsView
                HeaderLogo={HeaderLogo}
                siteId={siteId}
                phoneE164={phoneE164}
                registrationNumber={registrationNumber}
                address={address}
                onChangeRegistrationNumber={setRegistrationNumberUpper}
                onChangeAddress={setAddress}
                onBack={() => setFlow(Flow.WELCOME)}
                onContinue={() => {
                    setPassCode("");
                    setPurchasedPassId(0);
                    setExtendPassId(0);
                    setPaidNights(0);
                    setPaidTotal(0);
                    setArrivalDate(new Date());
                    setFlow(Flow.PURCHASE);
                }}
                onSelectExistingPass={(p) => {
                    setPurchasedPassId(Number(p.id));
                    if (p.userName) setName(p.userName);
                    if (p.userPhone) setPhoneE164(p.userPhone);

                    setRegistrationNumber(p.carRegistration || "");
                    setAddress(p.carAddress || "");
                    setPassCode(p.code || "");

                    setPaidNights(Number(p.duration || 0));
                    setPaidTotal(Number(p.costCents || 0) / 100);

                    setArrivalDate(new Date());
                    setFlow(Flow.SUCCESS);
                }}
            />
        );
    }

    if (flow === Flow.PURCHASE) {
        return (
            <PassPurchaseStepView
                HeaderLogo={HeaderLogo}
                siteId={siteId}
                name={name}
                phoneE164={phoneE164}
                registrationNumber={registrationNumber}
                address={address}
                setRegistrationNumber={setRegistrationNumberUpper}
                setAddress={setAddress}
                extendPassId={extendPassId}
                setExtendPassId={setExtendPassId}
                passCode={passCode}
                setPassCode={setPassCode}
                maxDuration={maxDuration}
                passes={passes}
                onConfirmPayment={handlePurchaseSuccess}
                onBack={() => setFlow(Flow.EXTRA)}
            />
        );
    }

    // SUCCESS
    return (
        <PassSuccessView
            siteId={siteId}
            HeaderLogo={HeaderLogo}
            name={name}
            phoneE164={phoneE164}
            registrationNumber={registrationNumber}
            nights={paidNights}
            totalPaid={paidTotal}
            arrivalDate={arrivalDate}
            siteTitle={siteTitle}
            passCode={passCode}
            passId={purchasedPassId}
            onViewTerms={() => window.open("https://www.autech.com.au/unlockwc-tc", "_blank")}
            onAddToWallet={() => {
                if (passCode) window.open(`https://pass.wc.slbk.au/upass/${passCode}`, "_blank");
            }}
            onDone={resetAll}
            onExtendPass={() => {
                setExtendPassId(purchasedPassId);
                setFlow(Flow.PURCHASE);
            }}
        />
    );
}
